import type { ReactNode } from 'react'
import { Text } from '@/ui'
import { mtc, TypographyStyles } from '@/styles'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { CADENCE_SUFFIX, formatPlanDate } from './lib/helpers'
import { CARD_RADIUS } from './PlanCardParts'
import type {
  CurrentPlan,
  PlanCadence,
  PlanChangeTarget,
} from './lib/planChange.types'

export interface PriceComparisonProps {
  currentPlan: CurrentPlan
  target: PlanChangeTarget
  footnote?: ReactNode
}

interface RateRowProps {
  label: string
  cadence: PlanCadence
  effectiveAmountCents: number
  listAmountCents: number
}

const RateRow = ({
  label,
  cadence,
  effectiveAmountCents,
  listAmountCents,
}: RateRowProps) => (
  <div className="flex items-start justify-between gap-4 px-5 py-4">
    <Text variant={TypographyStyles.BODY_MD} className="text-neutral-600">
      {label}
    </Text>
    <div className="flex flex-col items-end gap-0.5">
      <Text variant={TypographyStyles.LABEL_LG}>
        {formatCurrencyFromCents(effectiveAmountCents)}
        {CADENCE_SUFFIX[cadence]}
      </Text>
      {effectiveAmountCents !== listAmountCents && (
        <Text
          variant={TypographyStyles.BODY_SM}
          className="line-through text-neutral-400"
        >
          {formatCurrencyFromCents(listAmountCents)}
          {CADENCE_SUFFIX[cadence]}
        </Text>
      )}
    </div>
  </div>
)

export const PriceComparison = ({
  currentPlan,
  target,
  footnote,
}: PriceComparisonProps) => {
  const targetPriceCents = target.promo
    ? target.promo.discountedAmountCents
    : target.effectiveAmountCents
  const afterLabel =
    target.timing === 'term_end'
      ? `Your new rate from ${formatPlanDate(currentPlan.termEndsAt)}`
      : 'Your new rate'

  return (
    <div
      className={mtc('overflow-hidden border border-neutral-100', CARD_RADIUS)}
    >
      <RateRow
        label="Your current rate"
        cadence={currentPlan.cadence}
        effectiveAmountCents={currentPlan.effectiveAmountCents}
        listAmountCents={currentPlan.listAmountCents}
      />
      <div className="h-px bg-neutral-100" />
      <RateRow
        label={afterLabel}
        cadence={target.cadence}
        effectiveAmountCents={targetPriceCents}
        listAmountCents={target.listAmountCents}
      />
      {footnote && (
        <div className="border-t border-neutral-100 bg-neutral-25 px-5 py-3">
          <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
            {footnote}
          </Text>
        </div>
      )}
    </div>
  )
}
